"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _lodash = _interopRequireDefault(require("lodash.isequal"));
var _papaparse = _interopRequireDefault(require("papaparse"));
var _PapaConfig = _interopRequireDefault(require("../../../PapaConfig"));
var _constants = require("../../constants");
var _BASE = _interopRequireDefault(require("../BASE"));
function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    default: obj
  };
}
const CSV_DESCRIPTION = _constants.FILE_TYPE_DESCRIPTIONS[_constants.KEY_CSV];
const isDefined = v => v !== undefined && v !== null;
const isDocumentPropertyValid = doc => {
  // handle undefined
  if (doc === undefined) {
    return true;
  }

  // handle wrong data type
  if (typeof doc !== 'object') {
    return false;
  }

  // check root props
  const requiredRootProps = ['data', 'meta', 'errors'];
  const docRootProps = Object.keys(doc);
  const hasRequiredRootProps = requiredRootProps.every(p => docRootProps.indexOf(p) > -1);
  if (!hasRequiredRootProps) {
    return false;
  }

  // no items are ok, just means empty doc
  const hasData = doc.data instanceof Array && doc.data.length > 0;
  if (!hasData) {
    return true;
  }

  // check first item for format, if passes assume following items will pass
  const firstItem = doc.data[0];
  return typeof firstItem === 'object';
};
const isValidFileName = fname => {
  const parts = (fname || '').trim().split('.');
  return parts.length > 1 && parts[parts.length - 1].toLowerCase() === 'csv';
};
const CSVDocType = {
  ..._BASE.default,
  // @required overwrite
  toFileText: function () {
    let {
      document: doc = {}
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _papaparse.default.unparse(doc, _PapaConfig.default.unparse).trim();
  },
  // @required overwrite
  getFileParams: docJson => {
    if (CSVDocType.isValidDocJson(docJson)) {
      const text = CSVDocType.toFileText(docJson);
      const name = CSVDocType.getFileName(docJson);
      const ext = CSVDocType.getFileExtension(docJson);
      const filename = [name, ext].filter(v => !!v).join('.');
      return {
        filename,
        content: text,
        mimeType: 'text/csv'
      };
    }
    return undefined;
  },
  // @required overwrite
  toDocJson: _ref => {
    let {
      fileName,
      content
    } = _ref;
    let papaCsvJson;
    if (isDefined(content)) {
      papaCsvJson = typeof content === 'string' ? _papaparse.default.parse(content, _PapaConfig.default.parse) : content;
    }
    if (isValidFileName(fileName)) {
      const parts = (fileName || '').trim().split('.');
      const ext = parts.splice(parts.length - 1, 1)[0].toLowerCase();
      const name = parts.join('.').trim();
      return {
        type: _constants.KEY_CSV,
        fileName: name,
        fileExtension: ext,
        document: papaCsvJson
      };
    }
    return undefined;
  },
  isValidDocJson: function () {
    let docJson = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const docType = (CSVDocType.getDocType(docJson) || '').toUpperCase();
    const fileExtension = (CSVDocType.getFileExtension(docJson) || '').toLowerCase();
    const doc = CSVDocType.getDocument(docJson);
    return _BASE.default.isValidDocJson(docJson) && docType === CSV_DESCRIPTION.fileType && fileExtension === CSV_DESCRIPTION.fileExtension && isDocumentPropertyValid(doc);
  },
  createItemDocId: (item, _ref2) => {
    let {
      prop,
      props
    } = _ref2;
    let idProps;
    if (prop !== undefined) {
      idProps = [prop];
    } else if (props !== undefined) {
      idProps = props; // assume an array of props
    }
    return idProps.map(p => item[p]).join('~');
  },
  createItemMap: function () {
    let {
      document: doc = {}
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let {
      idProp,
      idProps
    } = arguments.length > 1 ? arguments[1] : undefined;
    const {
      data = []
    } = doc;
    return data.reduce((acc, item) => {
      const docId = CSVDocType.createItemDocId(item, {
        prop: idProp,
        props: idProps
      });
      acc[docId] = item;
      return acc;
    }, {});
  },
  computeDiffItemSets: (docJsonA, docJsonB, _ref3) => {
    let {
      idProp,
      idProps
    } = _ref3;
    const itemMapA = CSVDocType.createItemMap(docJsonA, {
      idProp,
      idProps
    });
    const docIdsA = Object.keys(itemMapA);
    const itemMapB = CSVDocType.createItemMap(docJsonB, {
      idProp,
      idProps
    });
    const docIdsB = Object.keys(itemMapB);
    const {
      deletedItems,
      sharedIds
    } = docIdsA.reduce((acc, idA) => {
      if (itemMapB[idA] === undefined) {
        acc.deletedItems.push(itemMapA[idA]);
      } else {
        acc.sharedIds.push(idA);
      }
      return acc;
    }, {
      deletedItems: [],
      sharedIds: []
    });
    const addedItems = docIdsB.reduce((acc, idB) => {
      if (itemMapA[idB] === undefined) {
        acc.push(itemMapB[idB]);
      }
      return acc;
    }, []);
    const modifiedItems = sharedIds.reduce((acc, id) => {
      if (!(0, _lodash.default)(itemMapA[id], itemMapB[id])) {
        acc.push(itemMapB[id]);
      }
      return acc;
    }, []);
    return {
      deleted: deletedItems,
      modified: modifiedItems,
      added: addedItems
    };
  },
  computeDiffDocsByItemSets: (docJsonA, docJsonB, _ref4) => {
    let {
      idProp,
      idProps
    } = _ref4;
    const fileNameA = docJsonA.fileName;
    const fileNameB = docJsonB.fileName;
    const fileNameDeleted = `DELETED-FROM-${fileNameA}-TO-${fileNameB}.csv`;
    const fileNameModified = `MODIFIED-FROM-${fileNameA}-TO-${fileNameB}.csv`;
    const fileNameAdded = `ADDED-FROM-${fileNameA}-TO-${fileNameB}.csv`;
    const itemDiffs = CSVDocType.computeDiffItemSets(docJsonA, docJsonB, {
      idProp,
      idProps
    });
    const docJsonDeleted = CSVDocType.toDocJson({
      fileName: fileNameDeleted,
      content: {
        meta: docJsonA.document.meta,
        errors: [],
        data: itemDiffs.deleted
      }
    });
    const docJsonModified = CSVDocType.toDocJson({
      fileName: fileNameModified,
      content: {
        meta: docJsonB.document.meta,
        errors: [],
        data: itemDiffs.modified
      }
    });
    const docJsonAdded = CSVDocType.toDocJson({
      fileName: fileNameAdded,
      content: {
        meta: docJsonB.document.meta,
        errors: [],
        data: itemDiffs.added
      }
    });
    return {
      deleted: docJsonDeleted,
      modified: docJsonModified,
      added: docJsonAdded
    };
  }
};
var _default = CSVDocType;
exports.default = _default;